import type { CustomerProps } from '@/repositories/auth/AuthRepository.types';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { api } from '@/libs/axios/apiClient';
import { authKeys } from '@/config/keys';
import { PATHS } from '@/config/paths';
import {
  crossSharedRoutes,
  publicRoutes,
  canBePublicRoutes,
} from '@/config/routesConfig';
import { AuthRepository } from '@/repositories/auth/AuthRepository';
import { isPathnameInRoutes } from './array';
import { destroyCookie, getAllCookies, setCookie } from './cookies';
import { checkIsTokenValid, setSession } from './jwt';
import {
  appendQueryParamsToUrl,
  type DestructuredQueryParams,
  destructureQueryParams,
} from './queryParams';

export class AuthenticationHandler {
  private readonly cookies: Record<string, string>;
  private token: string;
  private readonly refreshToken: string;
  private readonly authRepository = new AuthRepository();

  constructor() {
    this.cookies = getAllCookies();
    this.token = this.cookies[authKeys.authToken];
    this.refreshToken = this.cookies[authKeys.authRefreshToken];
  }

  async handleQueryParams({
    token,
    token_decrypted,
    XCODE,
    xcode,
  }: DestructuredQueryParams): Promise<void> {
    const code = xcode || XCODE;
    if (code) {
      window.localStorage.setItem('xcode', code);
    }
    if (token) {
      await this.signInWithToken(token);
    }
    if (token_decrypted) {
      await this.signInWithToken(token_decrypted, false);
    }
  }

  async handleAuth(
    searchParams: ReadonlyURLSearchParams,
    hasToUpdate = false
  ): Promise<HandleAuthResult | undefined> {
    const params = destructureQueryParams(searchParams);
    await this.handleQueryParams(params);

    if (!this.token) {
      let redirectUrl = appendQueryParamsToUrl(PATHS.auth.login);

      if (
        [...publicRoutes, ...crossSharedRoutes].includes(
          window.location.pathname
        ) ||
        isPathnameInRoutes(window.location.pathname, canBePublicRoutes)
      ) {
        redirectUrl = appendQueryParamsToUrl(window.location.pathname);
      }

      return { status: false, redirect: redirectUrl.toString() };
    }

    return this.signInWithAPI(hasToUpdate);
  }

  async signInWithAPI(hasToUpdate = false): Promise<HandleAuthResult> {
    try {
      await this.handleInvalidToken();
      const user = await this.authRepository.getProfile();

      if (
        !user &&
        !(
          [...publicRoutes, ...crossSharedRoutes].includes(
            window.location.pathname
          ) || isPathnameInRoutes(window.location.pathname, canBePublicRoutes)
        )
      ) {
        setSession(false);
        const redirectUrl = appendQueryParamsToUrl(PATHS.auth.login);
        return { status: false, redirect: redirectUrl.toString() };
      }

      window.localStorage.setItem(authKeys.user, JSON.stringify(user));
      const redirectUrl =
        this.mountRedirectURLForAuthenticatedUser(hasToUpdate);

      return { status: true, redirect: redirectUrl.toString(), customer: user };
    } catch (error) {
      const pahToRedirect = [...publicRoutes, ...crossSharedRoutes].includes(
        window.location.pathname
      )
        ? window.location.pathname
        : PATHS.auth.login;

      const redirectUrl = appendQueryParamsToUrl(pahToRedirect);
      return { status: false, redirect: redirectUrl.toString() };
    }
  }

  async handleInvalidToken() {
    if (!checkIsTokenValid(this.token)) {
      await this.requestRefreshToken();
      window.location.reload();
    }
  }

  private mountRedirectURLForAuthenticatedUser(hasToUpdate = false) {
    if (hasToUpdate) {
      return appendQueryParamsToUrl(PATHS.profile.updateData);
    }

    if (
      publicRoutes.includes(window.location.pathname) &&
      !crossSharedRoutes.includes(window.location.pathname)
    ) {
      return appendQueryParamsToUrl(PATHS.welcome);
    }

    return appendQueryParamsToUrl(window.location.pathname);
  }

  async signInWithToken(token: string, decrypt = true) {
    try {
      let decrypted_token: string | null = null;
      destroyCookie('menu-versions');
      window.localStorage.removeItem('menu-versions');

      if (decrypt) {
        setSession(false);
        const { data } = await api.post<{ access_token: string }>(
          '/me/decrypt-token',
          { token }
        );
        decrypted_token = data.access_token;
      }

      const newToken = decrypted_token ?? token;
      setSession(newToken);
      setCookie(authKeys.authToken, newToken);
      this.setToken(decrypted_token ?? token);

      const customer = await this.authRepository.getProfile();

      return { status: true, redirect: PATHS.home, customer };
    } catch (error) {
      return { status: false, redirect: null, customer: null };
    }
  }

  private readonly requestRefreshToken = async () => {
    if (!this.refreshToken) {
      return;
    }

    const { data } = await api.post<{ access_token: string }>(
      'auth/v1/refresh-token',
      {},
      { headers: { Authorization: `Bearer ${this.refreshToken}` } }
    );
    setCookie(authKeys.authToken, data.access_token);
  };

  get getToken() {
    return this.token;
  }

  setToken(tokenValue: string, expires_in?: number) {
    setCookie(authKeys.authToken, tokenValue, expires_in);
    setSession(tokenValue);
    this.token = tokenValue;
  }

  clearAuth() {
    destroyCookie(authKeys.authToken);
    this.token = '';
    return true;
  }
}

interface HandleAuthResult {
  status: boolean;
  redirect: string;
  customer?: CustomerProps;
}
